/**
 * Date-only string handling shared by display formatting and curve math.
 *
 * Date-only values must never reach `new Date` unguarded: a bare `YYYY-MM-DD`
 * parses as UTC midnight while a local-timezone comparison shifts it by up to a
 * day, and a calendar-invalid value like `2026-02-30` silently rolls over in
 * some engines. Both hazards are load-bearing here — the same value is rendered
 * as a day and measured in years-to-maturity.
 */

/** Padded components of a real calendar date. */
interface DateOnlyParts {
  kind: "date";
  mm: string;
  dd: string;
  yyyy: string;
}

/**
 * Classify a date-only value (bare `YYYY-MM-DD` or a UTC-midnight timestamp such
 * as `YYYY-MM-DDT00:00:00Z`):
 * - `{ kind: "date", ... }` with padded components for a real calendar date, so
 *   callers can format without going through `new Date`, which would reinterpret
 *   a UTC-midnight value in the local timezone and shift the displayed day.
 * - `{ kind: "invalid" }` when the value looks date-only but is not a real date
 *   (e.g. `2026-02-30`), so callers render a dash instead of letting `new Date`
 *   silently roll it over to the next month.
 * - `null` for anything else (e.g. full timestamps with a non-midnight time),
 *   which callers should format via `new Date`.
 */
export function classifyDateOnly(
  value: string,
): DateOnlyParts | { kind: "invalid" } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?Z?)?$/.exec(value);
  if (!match) return null;
  const [, yyyy, mm, dd] = match;
  const year = Number(yyyy);
  const month = Number(mm);
  const day = Number(dd);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return { kind: "invalid" };
  }
  return { kind: "date", mm, dd, yyyy };
}

/**
 * A date string as a `Date`, resolved without local-timezone drift: date-only
 * values land on UTC midnight, calendar-invalid date-only values resolve to
 * `null` rather than rolling over, and values carrying a real time of day are
 * parsed as-is (already unambiguous).
 */
export function parseDateUtc(value: string | null | undefined): Date | null {
  if (!value) return null;

  const parts = classifyDateOnly(value);
  if (parts?.kind === "invalid") return null;
  if (parts) {
    return new Date(Date.UTC(Number(parts.yyyy), Number(parts.mm) - 1, Number(parts.dd)));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
